import { RootStore } from "./rootStore";
import { observable, computed, action, runInAction, reaction } from "mobx";
import { IUser, ILogin, IGuest, ICustomer, ICustomerForm, IResetPassword, IForgotPassword, ICustomerProfile } from "../models/user";
import agent from "../api/agent";
import { history } from "../";
import { MD5 } from "crypto-js";
import { ICustomerCard, INewCard, INewStripeCard } from "../models/cards";
import { IApiResponse, IApiResponseT } from "../models/apiresponse";
import { IVoucherListItem } from "../models/voucher";
import { IPriceMatrixItem } from "../models/category";
import { IStripeIntent } from "../models/stripe_models";
export default class UserStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    reaction(
      () => this.user,
      (user) => {
        if(user){
          window.sessionStorage.setItem("mdc_cus",user.isCustomer ? "1" : "0");
          window.sessionStorage.setItem("mdc_pc",user.postCode);
        }
        else{
          window.sessionStorage.removeItem("mdc_cus");
          window.sessionStorage.removeItem("mdc_pc");
        }
      }
    )
  }

  @observable user: IUser | null = null;
  @observable customer: ICustomer | null = null;
  @observable customerCards: ICustomerCard[] = [];
  @observable showLogin: boolean = false;
  @observable showForgotPassword: boolean = false;
  @observable defaultCardUID: string = "";
  @observable showCardDialog: boolean = false;
  @observable savingCard: boolean = false;
  @observable cardSaveError: string = "";
  @observable passwordVerified: boolean = false;
  @observable loadingMyOffers: boolean = false;
  @observable myOffers: IVoucherListItem[] = [];
  @observable loadingMyPriceGrid: boolean = false;
  @observable myPriceGrid: IPriceMatrixItem[] = [];
  @observable stripeIntent: IStripeIntent | undefined;

  @action setPasswordVerified = (value: boolean) => {
    this.passwordVerified = value;
  }
  @action setCardSaveError = (error: string) => {
    this.cardSaveError = error;
  }
  @action setDefaultCard = (card: ICustomerCard) => {
    this.customerCards.forEach((c) =>{
      if(c.uniqueRecId === card.uniqueRecId){
        runInAction(() =>{
          this.defaultCardUID = card.uniqueRecId;
          card.isDefault = true;
        })
      }
      else
      runInAction(() => card.isDefault = false );
    })
  }
  @computed get isLoggedIn() {
    return !!this.user;
  }
  @computed get isCustomer() {
    return this.user ? this.user?.isCustomer : false;
  }
  @action setShowLogin = (value: boolean) => {
    this.showLogin = value;
  }
  @action showForgotPasswordDialog = () =>{
    this.showForgotPassword = true;
  }
  @action hideForgotPasswordDialog = () =>{
    this.showForgotPassword = false;
  }
  @action showNewCardDialog = () => {
    this.showCardDialog = true;
  } 
  @action hideNewCardDialog = () =>{
    this.showCardDialog = false;
  }
  @action login = async (values: ILogin) => {
    this.rootStore.commonStore.setApiError(undefined);
    try {
      this.rootStore.commonStore.addErrorLog("login",`Function called with payload ${JSON.stringify(values)}`,false)
      const user = await agent.User.login(values);
      if(user.isAllowed){
        if(!user.resetKey){
          runInAction(() => {
            this.user = user;
          });
          this.setShowLogin(false);
          this.rootStore.commonStore.setToken(user.token);
          if (!this.rootStore.commonStore.prevRoute){
            if(this.rootStore.orderStore.orderDetails.length > 0)
              this.rootStore.orderStore.reCalculatePrices();
            else
              if (this.rootStore.commonStore.currentShop?.itemSelectionOptions === 0)
                history.push("/basket");
              else
                history.push("/order");
          }
          else {
            switch(this.rootStore.commonStore.prevRoute){
              case '/order': 
                history.push("/order");
                break;
              case '/newcustomer':
                history.push("/basket");
                break;
              case '/basket':
                history.push("/basket");
                break;
            }
          }
        } else {
          /// Reset Key
          history.push(`/resetpassword?key=${user.resetKey}&message=${user.errorMessage}`)
        }
      } else {
        // Not Allowed
        throw({notAllowed: true, error: user.errorMessage})
      }
    } catch (error) {
      this.rootStore.commonStore.setApiError(error?.toString());
      this.rootStore.commonStore.addErrorLog("login",error)
      throw(error?.toString())
    } finally {
      this.rootStore.commonStore.addErrorLog("login",'Function call end',false)
    }
  };

  @action logout = () => {
    this.rootStore.commonStore.setToken(null);
    this.user = null;
    this.customer = null;
    this.rootStore.orderStore.clearOrderDetails();
    this.rootStore.orderStore.clearOrder();
    this.rootStore.orderStore.clearBasketValues();
    this.rootStore.timeSlotStore.clearTimeslots();
    this.rootStore.commonStore.setPrevRoute("");
    history.push(`/${this.rootStore.commonStore.shopUID}`);
  };

  @action signIn = () => {
    if(window.location.pathname.toString() !== "/resetpassword" && window.location.pathname.toString() !== "/newcustomer")
      this.rootStore.commonStore.setPrevRoute(window.location.pathname.toString());
    this.rootStore.commonStore.setToken(null);
    this.user = null;
    this.customer = null;
    this.showLogin = true;
    history.push(`/${this.rootStore.commonStore.shopUID}`);
  }

  @action guest = async (values: IGuest) => {
    this.rootStore.commonStore.setApiError(undefined);
    this.rootStore.commonStore.addErrorLog("guest",`Function called with payload ${JSON.stringify(values)}`,false)
      agent.User.guest(values).then((user) => {
        runInAction(() => this.user = user);
        if(user){
          this.rootStore.commonStore.setToken(user.token);
          this.rootStore.orderStore.clearOrderDetails();
          this.rootStore.orderStore.clearOrder();
          this.rootStore.orderStore.clearBasketValues();
          this.rootStore.timeSlotStore.clearTimeslots();
          if (this.rootStore.commonStore.currentShop?.itemSelectionOptions === 0) {
            history.push("/basket");
            this.rootStore.commonStore.setPrevRoute("/basket");
          } else {
            history.push("/order");
            this.rootStore.commonStore.setPrevRoute("/order");
          }
        }
        else
          this.rootStore.commonStore.setApiError("Sorry, we don't cover your postcode yet!")
        
      }).catch((error) => {
        this.rootStore.commonStore.setApiError("Sorry, we don't cover your postcode yet!")
        this.rootStore.commonStore.addErrorLog("guest",error)
      }).finally(() => this.rootStore.commonStore.addErrorLog("guest",'Function call end',false));
  };

  @action getUser = async () => {
    try {
      this.rootStore.commonStore.addErrorLog("getUser",'Function called',false)
      const user = await agent.User.current();
      runInAction(() => {
        this.user = user;
      });
    } catch (error) {
      this.rootStore.commonStore.addErrorLog("getUser",error);
    } finally {
      this.rootStore.commonStore.addErrorLog("getUser",'Function call end',false)
    }
  };

  @action customerInfo = async () => {
    try {
      this.rootStore.commonStore.addErrorLog("customerInfo",'Function called',false)
      const customer = await agent.Customer.customer();
      runInAction(() => {
        this.customer = customer;
      });
    } catch (error) {
      runInAction(() => (this.customer = null));
      this.rootStore.commonStore.addErrorLog("customerInfo",error);
    } finally {
      this.rootStore.commonStore.addErrorLog("customerInfo",'Function call end',false)
    }
  };
  @action saveCustomer = async (values: ICustomerForm) => {
    this.rootStore.commonStore.setApiError(undefined)
    try {
          this.rootStore.commonStore.addErrorLog("saveCustomer",`Function called with payload ${JSON.stringify(values)}`,false)
          agent.Customer.savecustomer({
          accountCode: "",
          title: "",
          firstName: values.firstName,
          middleName: values.middleName,
          surname: values.surname,
          address1: values.address1,
          address2: values.address2,
          address3: values.address3,
          address4: values.address4,
          postCode: values.postCode,
          mobileNo: `0${values.mobileNo.substr(-10,10)}`,
          emailId: values.emailId,
          password: MD5(values.password).toString().toUpperCase(),
          deliveryNotes: values.deliveryNotes,
          membershipType: '',
          cardCustomerId: ''
        }).then((user) => {
          runInAction(() => {
            this.user = user;
          });
          this.rootStore.commonStore.setToken(null);
          this.rootStore.commonStore.setToken(user.token);
        });
    } catch (error) {
      this.rootStore.commonStore.setApiError(error?.toString());
      this.rootStore.commonStore.addErrorLog("saveCustomer",error);
    } finally {
      this.rootStore.commonStore.addErrorLog("saveCustomer",'Function call end',false)
    }
  };

  @action updateCustomer =  async (customer: ICustomerProfile) => {
    this.rootStore.commonStore.setApiError(undefined)
    this.rootStore.commonStore.addErrorLog("updateCustomer",`Function called with payload ${JSON.stringify(customer)}`,false)
    agent.Customer.updatecustomer(customer).then((res) => {
      if(res){
        this.updateDefaultCustomerCard()
        this.customerInfo();
      }
    }).catch((err) => {
      this.rootStore.commonStore.setApiError(err?.toString())
      this.rootStore.commonStore.addErrorLog("updateCustomer",err);
    }).finally(() => this.rootStore.commonStore.addErrorLog("updateCustomer",'Function call end',false));
  }

  @action verifyCustomer = async (mobileNo: string, emailId:string) : Promise<boolean> =>{
    this.rootStore.commonStore.addErrorLog("verifyCustomer",`Function called with payload Mobile No: ${mobileNo}, EmailId: ${emailId}`,false)
    try{
      return await agent.Customer.verifycustomer(mobileNo,emailId)
    } catch (err) {
      this.rootStore.commonStore.addErrorLog("verifyCustomer",err);
      throw (err)
    } finally {
      this.rootStore.commonStore.addErrorLog("verifyCustomer",'Function call end',false)
    }
  }

  @action verifyPassword = async(password: string) : Promise<boolean> => {
    this.rootStore.commonStore.addErrorLog("verifyPassword",`Function called with payload ${password}`,false)
    try {
      return await agent.User.verifypassword(password); 
    } catch (err) {
      this.rootStore.commonStore.addErrorLog("verifyPassword",err);
      throw (err)
    } finally {
      this.rootStore.commonStore.addErrorLog("verifyPassword",'Function call end',false)
    }
  }

  @action doForgotPassword = async (values: IForgotPassword) : Promise<IApiResponse> => {
    try{
      this.rootStore.commonStore.addErrorLog("doForgotPassword",`Function called with payload ${JSON.stringify(values)}`,false)
      return await agent.User.forgotpassword(values);
    } catch(error){
        this.rootStore.commonStore.addErrorLog("doForgotPassword",error);
        if(error) 
          return {
            status: 0,
            error: {
              code: 101,
              description: "The email id is not registered. Please enter your registered email id."
            }
          }; 
        throw error;} finally {
          this.rootStore.commonStore.addErrorLog("doForgotPassword",'Function call end',false)
        }
  }

  @action resetPassword = async (values: IResetPassword) : Promise<IApiResponse> => {
    try{
      this.rootStore.commonStore.addErrorLog("resetPassword",`Function called with payload ${JSON.stringify(values)}`,false)
      return await agent.User.resetpassword(values);
    } catch(error) { 
      this.rootStore.commonStore.addErrorLog("resetPassword",error);
      if (error) return {
            status: 0,
            error: {
              code: 500,
              description: "An error occured while resetting your password"
            }
           }
          throw error;} finally {
          this.rootStore.commonStore.addErrorLog("resetPassword",'Function call end',false)
        }
  }
  
  @action createStripeIntent = async () => {
    this.savingCard = true;
    this.rootStore.commonStore.addErrorLog("createStripeIntent",'Function called',false)
    agent.CustomerCards.createstripeintent()
              .then((response) => {
                if(response.status === 1 ){
                  runInAction(() => {
                    this.stripeIntent = response.result;
                    this.savingCard = false;
                  })
                if(this.customer!.cardCustomerId === ''){
                  this.customerInfo();
                }
                }
                else {
                  runInAction(() => {
                            this.savingCard = false;
                            this.cardSaveError = response.error.description;
                      })
                } 
              }).catch((err) => this.rootStore.commonStore.addErrorLog("createStripeIntent",err))
              .finally(() => this.rootStore.commonStore.addErrorLog("createStripeIntent",'Function call end',false))
  }

  @action cancelStripeIntent = async () => {
    if(this.stripeIntent){
      try {
        this.rootStore.commonStore.addErrorLog("cancelStripeIntent",'Function called',false)
        await agent.CustomerCards.cancelstripeintent(this.stripeIntent.id);
      } catch(error) { 
      this.rootStore.commonStore.addErrorLog("cancelStripeIntent",error);
      } finally {
          this.rootStore.commonStore.addErrorLog("cancelStripeIntent",'Function call end',false)
        }
    }
  }

  @action saveNewStripeCard = async (id: string) => {
    this.savingCard = true;
    this.rootStore.commonStore.addErrorLog("saveNewStripeCard",`Function called with payload ${id}`,false)
    var card: INewStripeCard = { id };
    await agent.CustomerCards.addnewstripecard(card).then((response:IApiResponseT<ICustomerCard>) =>{
    if(response.status === 1){
        runInAction(() => {
          this.stripeIntent = undefined;
          this.customerCards = [...this.customerCards,response.result];
          this.cardSaveError = "";
          this.savingCard = false;
          this.hideNewCardDialog();
        })
        this.setDefaultCard(response.result);
      }
      else{
        runInAction(() => {
          this.stripeIntent = undefined;
          this.savingCard = false;
          if(response.error)
          this.cardSaveError = response.error.description;
        })
      }
    }).catch((err) => this.rootStore.commonStore.addErrorLog("saveNewStripeCard",err))
    .finally(() => this.rootStore.commonStore.addErrorLog("saveNewStripeCard",'Function call end',false))  
  }

  @action getCustomerCards = async () => {
    this.rootStore.commonStore.addErrorLog("getCustomerCards",'Function called',false)
    agent.CustomerCards.customercards().then((cards:ICustomerCard[]) => {
      runInAction(() =>{
        this.customerCards = cards;
        if(cards.length > 0){
          var card = cards.find(card => card.isDefault === true);
          if(card)
            this.defaultCardUID = card.uniqueRecId;
        }
      })
    } ).catch((err) => this.rootStore.commonStore.addErrorLog("getCustomerCards",err))
    .finally(() => this.rootStore.commonStore.addErrorLog("getCustomerCards",'Function call end',false));
  }

  @action saveNewCard = async (card: INewCard) => {
    this.savingCard = true;
    this.rootStore.commonStore.addErrorLog("saveNewCard",`Function called with payload ${JSON.stringify(card)}`,false)
    agent.CustomerCards.addnewcard(card).then((response:IApiResponseT<ICustomerCard>) =>{
      if(response.status === 1){
        runInAction(() => {
          this.customerCards = [...this.customerCards,response.result];
          this.cardSaveError = "";
          this.savingCard = false;
          this.hideNewCardDialog();
        })
        this.setDefaultCard(response.result);
      }
      else{
        runInAction(() => {
          this.savingCard = false;
          this.cardSaveError = response.error.description;
        })
      }
    }).catch((err) => this.rootStore.commonStore.addErrorLog("saveNewCard",err))
    .finally(() => this.rootStore.commonStore.addErrorLog("saveNewCard",'Function call end',false));
  }

  @action updateDefaultCustomerCard = async () => {
    var card = this.customerCards.filter(c => c.uniqueRecId === this.defaultCardUID)[0];
    if(card)
      agent.CustomerCards.setdefaultcard(card);
  }

  @action deactivateCustomerCard = async (card: ICustomerCard) => {
    const cardUID = card.uniqueRecId;
    this.rootStore.commonStore.addErrorLog("deactivateCustomerCard",`Function called with payload ${JSON.stringify(card)}`,false)
    await agent.CustomerCards.deactivatecard(card).then(()=>{
      runInAction(() => {
          if(cardUID === this.defaultCardUID)    
            this.defaultCardUID = "";
          this.customerCards = [...this.customerCards.filter(c => c.uniqueRecId !== card.uniqueRecId)];
          this.cardSaveError = "";
          this.savingCard = false;
        }
    )
  }).catch((err) => this.rootStore.commonStore.addErrorLog("deactivateCustomerCard",err))
    .finally(() => this.rootStore.commonStore.addErrorLog("deactivateCustomerCard",'Function call end',false));
}

@action loadMyOffers = async () => {
  this.loadingMyOffers = true;
  this.rootStore.commonStore.addErrorLog("loadMyOffers",'Function called',false)
  await agent.Customer.myoffers().then((offers) => {
    runInAction(() =>{
      this.myOffers = offers;
      this.loadingMyOffers = false;
    })
  }).catch((err) => this.rootStore.commonStore.addErrorLog("loadMyOffers",err))
    .finally(() => this.rootStore.commonStore.addErrorLog("loadMyOffers",'Function call end',false));
}

@action loadMyPriceGrid = async () => {
  this.loadingMyPriceGrid = true;
   this.rootStore.commonStore.addErrorLog("loadMyPriceGrid",'Function called',false)
  await agent.Customer.myspecialprice().then((pricematrix) => {
    runInAction(()=>{
      this.myPriceGrid = pricematrix;
      this.loadingMyPriceGrid = false;
    })
  }).catch((err) => this.rootStore.commonStore.addErrorLog("loadMyPriceGrid",err))
    .finally(() => this.rootStore.commonStore.addErrorLog("loadMyPriceGrid",'Function call end',false));
}
}
